import * as React from 'react';
import { useCallback } from 'react';
import Amenities from '../../../Amenities';
import { FlightSegmentStatus, SegmentWithTransferFragment, SegmentStopFragment } from '@websky/graphql';
import { useTheme } from '../../../theme';
import cn from 'classnames';
import FlightInfo, { Props as FlightInfoProps } from './FlightInfo/FlightInfo';
import MediaQuery from 'react-responsive';
import { TABLET_MIN_WIDTH } from '../../../utils';
import { useTranslation } from 'react-i18next';
import { InfoMessagePositionsEnum, SegmentFragment } from '@websky/graphql';
import { useConfig } from '../../../context';
import AircraftLivery from '../../../AircraftLivery';
import InfoMessages from '../../../InfoMessages';
import RouteInfo from './RouteInfo/RouteInfo';

export interface Renderers {
	renderFlightInfo?: (flightInfoProps: FlightInfoProps) => React.ReactElement;
	renderPlaneInfo?: (flightInfoProps: SegmentFragment) => React.ReactNode;
}

export interface Props extends Renderers {
	segment: SegmentWithTransferFragment;
	className?: string;
	first?: boolean;
	last?: boolean;
	stops?: SegmentStopFragment[];
	singleFlight?: boolean;
	nextDay?: boolean;
	multipleSegments?: boolean;
	stopsSeparator?: string;
	isReturnFlight?: boolean;
}

const Segment: React.FC<Props> = props => {
	const {
		segment: segmentWithTransfer,
		className,
		last,
		stops = [],
		nextDay,
		renderFlightInfo,
		multipleSegments,
		renderPlaneInfo,
		isReturnFlight
	} = props;

	const segment = segmentWithTransfer.segment;
	const canceled = segment.status === FlightSegmentStatus.Canceled;

	const iataCode = useConfig().global.companyInfo.iataCode;
	const hasOperatingCarrier = ['A9', 'RM'].includes(iataCode);

	const operationCarrier =
		hasOperatingCarrier && segment.marketingAirline.iata !== segment.operatingAirline.iata
			? segment.operatingAirline.name
			: null;

	const theme = useTheme('DesktopFlightRoute').SegmentStyles;
	const { t } = useTranslation('DesktopFlightInfo');

	const renderPlaneInfoHandler = useCallback(() => {
		if (renderPlaneInfo) {
			return renderPlaneInfo(segment);
		}

		return (
			<div className={theme.planeInfo}>
				<div className={theme.aircraft}>{segment.aircraft.name}</div>

				{segment?.flightInfo?.amenities?.length > 0 && (
					<div className={theme.amenities}>
						<Amenities amenities={segment.flightInfo.amenities} />
					</div>
				)}
			</div>
		);
	}, [segment, renderPlaneInfo]);

	return (
		<>
			<div
				className={cn(
					theme.segment,
					{
						[theme.canceled]: canceled,
						[theme.oneSegment]: stops.length > 0 && !canceled
					},
					className
				)}
			>
				{renderFlightInfo ? (
					renderFlightInfo({ segment, nextDay, singleFlight: false })
				) : (
					<FlightInfo segment={segment} nextDay={nextDay} />
				)}

				<RouteInfo segmentWithTransfer={segmentWithTransfer} isCanceled={canceled} {...props} />

				<MediaQuery minWidth={TABLET_MIN_WIDTH}>
					<div className={theme.planeContainer}>
						{segment.aircraft && (
							<>
								<AircraftLivery
									className={theme.plane}
									returnClassName={theme.plane_return}
									aircraft={segment.aircraft}
									isReturnFlight={isReturnFlight}
								/>

								{renderPlaneInfoHandler()}

								{operationCarrier && (
									<div className={theme.operatingCarrier}>
										{t('Operating carrier')}: {operationCarrier}
									</div>
								)}
							</>
						)}
					</div>
				</MediaQuery>
			</div>

			{!last && multipleSegments && (
				<InfoMessages
					position={InfoMessagePositionsEnum.stopPoint}
					className={theme.stopPointInfoMessage}
					itemHtmlClassName={theme.stopPointInfoMessage__htmlMessage}
					messages={segment.messages}
				/>
			)}
		</>
	);
};

export default Segment;
